import React from 'react';
import { Link } from 'react-router-dom';
import { Home, HelpCircle, MessageCircle } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

export const NotFoundPage: React.FC = () => {
    usePageMetadata({
        title: "404: Strona nie znaleziona | BiznesBezKlikania.pl",
        description: "Przepraszamy, ale strona, której szukasz, nie istnieje. Wróć na stronę główną, aby znaleźć interesujące Cię rozwiązania AI.",
    });

    return (
        <div className="relative min-h-[calc(100vh-192px)] flex items-center justify-center overflow-hidden">
            <ParticlesBackground variant="minimal" />
            <AnimatedBlob variant="purple" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

            <div className="relative z-10 text-center px-4 py-8">
                <RevealOnScroll>
                    <h1 className="text-8xl md:text-9xl lg:text-[12rem] font-extrabold bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient tracking-tighter">
                        404
                    </h1>
                </RevealOnScroll>

                <RevealOnScroll delay={100}>
                    <h2 className="text-3xl md:text-4xl font-bold mt-4 text-text-primary-light dark:text-text-primary-dark">
                        Strona nie została znaleziona
                    </h2>
                </RevealOnScroll>

                <RevealOnScroll delay={200}>
                    <p className="mt-4 max-w-md mx-auto text-text-secondary-light dark:text-text-secondary-dark">
                        Przepraszamy, ale strona, której szukasz, nie istnieje, została przeniesiona lub jest tymczasowo niedostępna.
                    </p>
                </RevealOnScroll>

                <RevealOnScroll delay={300}>
                    <Link
                        to="/"
                        className="mt-8 px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 inline-flex items-center gap-2"
                        aria-label="Wróć do strony głównej"
                    >
                        <Home className="w-5 h-5" />
                        Wróć na stronę główną
                    </Link>
                </RevealOnScroll>

                <RevealOnScroll delay={400}>
                    <GlassCard variant="subtle" className="mt-12 max-w-md mx-auto">
                        <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">
                            Może szukasz czegoś innego?
                        </h3>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="/spolecznosc/faq"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 dark:bg-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-colors text-primary-light dark:text-primary-dark font-medium"
                            >
                                <HelpCircle className="w-4 h-4" />
                                FAQ
                            </Link>
                            <Link
                                to="/kontakt"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 dark:bg-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-colors text-primary-light dark:text-primary-dark font-medium"
                            >
                                <MessageCircle className="w-4 h-4" />
                                Kontakt
                            </Link>
                        </div>
                    </GlassCard>
                </RevealOnScroll>
            </div>
        </div>
    );
};
